import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Users,
  UtensilsCrossed,
  Pill,
  Home as HomeIcon,
  Car,
  HandHeart,
  CheckCircle2,
  ArrowRight,
  Phone
} from "lucide-react";
import { Button } from "../components/ui/button";
import { services, companyInfo } from "../data/mockData";
import SectionHeader from "../components/SectionHeader";

const iconMap = {
  Users,
  UtensilsCrossed,
  Pill,
  Home: HomeIcon,
  Car,
  HandHeart
};

const Services = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash]);

  return (
    <div data-testid="services-page" className="bg-stone-50">
      {/* HERO */}
      <section className="relative bg-amber-50/30 py-20 lg:py-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-5">
              Our Services
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
              A full circle of <span className="italic text-amber-700">gentle support</span>.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Every service we offer is designed around one idea: treating your loved one the way we'd treat our own family. Explore how we can help.
            </p>
          </div>
        </div>
        <div className="absolute right-0 top-10 w-48 h-48 opacity-10 text-amber-700 pointer-events-none hidden lg:block">
          <svg viewBox="0 0 100 100" fill="currentColor">
            <circle cx="50" cy="50" r="50" />
          </svg>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 lg:space-y-32">
          {services.map((service, i) => {
            const Icon = iconMap[service.icon] || Users;
            const isEven = i % 2 === 0;
            return (
              <div
                id={service.id}
                key={service.id}
                data-testid={`service-section-${service.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-24"
              >
                {/* Image */}
                <div className={`lg:col-span-6 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
                  <div className="aspect-[4/3] rounded-[2rem] overflow-hidden bg-amber-50">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`lg:col-span-6 ${isEven ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="inline-flex w-14 h-14 rounded-2xl bg-amber-700 items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <p className="text-amber-700 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
                    Service 0{i + 1}
                  </p>
                  <h2 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight mb-6">
                    {service.title}
                  </h2>
                  <p className="text-slate-600 text-lg leading-relaxed mb-8">
                    {service.fullDescription}
                  </p>

                  <div className="space-y-4 mb-10">
                    {service.features.map((feat, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                        <p className="text-slate-700">{feat}</p>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button
                      data-testid={`service-cta-${service.id}`}
                      className="bg-amber-700 hover:bg-amber-800 text-white rounded-full px-7 py-5 font-medium"
                    >
                      Discuss this service
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-script text-4xl text-amber-400 mb-5">Not sure where to start?</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-6">
            We'll build a care plan around you.
          </h2>
          <p className="text-stone-300 text-lg leading-relaxed mb-10">
            Services can be combined or customized for any schedule — a few hours, overnight, or full-time. Let's talk about what your family needs most.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 w-full sm:w-auto">
                <Phone className="w-4 h-4 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 w-full sm:w-auto"
              >
                Request a consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
